import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { House } from "@phosphor-icons/react";

export default function Register() {
  const { register, error } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next") || "/";
  const prefillEmail = params.get("email") || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await register(email, password, name);
    setBusy(false);
    if (ok) navigate(nextPath);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-sand-100">
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1638780506095-3d61a4f0edd6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxvcmdhbml6ZWQlMjBzdG9yYWdlJTIwYm94ZXMlMjBzaGVsZnxlbnwwfHx8fDE3ODE3MjQxNzB8MA&ixlib=rb-4.1.0&q=85"
          alt="Organized storage"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-stoneink-900/55 via-stoneink-900/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="font-heading text-4xl xl:text-5xl font-semibold leading-tight">Begin a calmer<br/>kind of home.</div>
          <p className="mt-4 text-white/85 max-w-md">Snap a photo, name the spot, and never lose a thing again.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-md">
          <Link to="/" data-testid="register-logo-home-link" className="flex items-center gap-3 mb-10 group w-fit">
            <div className="h-12 w-12 rounded-2xl bg-terracotta-500 text-white grid place-items-center shadow-ambient group-hover:shadow-hover transition-shadow">
              <House weight="duotone" size={26} />
            </div>
            <div>
              <div className="font-heading text-2xl font-semibold text-stoneink-900 group-hover:text-terracotta-600 transition-colors">Nestled</div>
              <div className="text-xs text-stoneink-500">welcome home</div>
            </div>
          </Link>
          <h1 className="font-heading text-4xl sm:text-5xl text-stoneink-900 font-semibold tracking-tight">Make yourself at home</h1>
          <p className="mt-3 text-stoneink-700">Create your inventory in under a minute.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Your name</label>
              <input
                data-testid="register-name-input"
                required value={name} onChange={(e) => setName(e.target.value)}
                className="mt-2 w-full h-12 px-4 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none"
                placeholder="Sam Wren"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Email</label>
              <input
                data-testid="register-email-input"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full h-12 px-4 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none"
                placeholder="you@home.app"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Password</label>
              <input
                data-testid="register-password-input"
                type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full h-12 px-4 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none"
                placeholder="At least 6 characters"
              />
            </div>
            {error && <div className="text-sm text-destructive" data-testid="register-error">{error}</div>}
            <button
              data-testid="register-submit-btn"
              type="submit" disabled={busy}
              className="w-full h-12 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium transition-all active:scale-[0.99] disabled:opacity-60"
            >
              {busy ? "Creating…" : "Create account"}
            </button>
          </form>

          <p className="mt-8 text-sm text-stoneink-700">
            Already nesting?{" "}
            <Link to="/login" data-testid="go-to-login" className="text-terracotta-600 font-medium hover:text-terracotta-700 underline-offset-4 hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
